import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, supabase } from '../../lib/api';
import { setPasswordRecoveryMode, useAuth } from '../../contexts/AuthContext';
import { Button, Input, Card } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';

const EXPIRED_LINK_MESSAGE =
  'This reset link is invalid or has expired. Request a new one and open it in the same browser you used to request it.';

function readRecoveryParams() {
  if (typeof window === 'undefined') {
    return {
      error: null,
      errorCode: null,
      errorDescription: '',
      type: null,
      access_token: null,
      refresh_token: null,
      code: null,
      token_hash: null,
    };
  }
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const search = new URLSearchParams(window.location.search);
  return {
    error: hash.get('error') || search.get('error'),
    errorCode: hash.get('error_code') || search.get('error_code'),
    errorDescription: (hash.get('error_description') || search.get('error_description') || '').replace(/\+/g, ' '),
    type: hash.get('type') || search.get('type'),
    access_token: hash.get('access_token'),
    refresh_token: hash.get('refresh_token'),
    code: search.get('code'),
    token_hash: search.get('token_hash') || hash.get('token_hash'),
  };
}

function hasRecoveryMaterials(params) {
  return Boolean(
    params.access_token ||
      params.code ||
      params.token_hash ||
      params.type === 'recovery',
  );
}

function stripAuthFragment() {
  if (typeof window === 'undefined') return;
  const { pathname } = window.location;
  window.history.replaceState(null, '', pathname);
}

const fullPageSpinner = (
  <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-50 text-gray-600 gap-3">
    <Spinner size="lg" className="text-primary h-8 w-8" />
    <p className="text-sm">Verifying your reset link…</p>
  </div>
);

export default function ResetPassword() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [phase, setPhase] = useState('loading');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  setPasswordRecoveryMode(true);

  useEffect(() => {
    setPasswordRecoveryMode(true);
    if (!supabase) {
      setPhase('error');
      setError('Password reset is not available.');
      return undefined;
    }

    const params = readRecoveryParams();
    let cancelled = false;
    let settled = false;
    let timeoutId;

    const finish = (ok, message) => {
      if (cancelled || settled) return;
      settled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      if (ok) {
        setPhase('form');
        stripAuthFragment();
      } else {
        setPhase('error');
        setError(message || EXPIRED_LINK_MESSAGE);
      }
    };

    if (params.error) {
      finish(false, params.errorDescription || EXPIRED_LINK_MESSAGE);
      return undefined;
    }

    const expectingRecovery = hasRecoveryMaterials(params);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) return;
      if (event === 'PASSWORD_RECOVERY') finish(true);
      if (event === 'SIGNED_IN' && expectingRecovery) finish(true);
    });

    const establish = async () => {
      try {
        if (params.access_token && params.refresh_token) {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: params.access_token,
            refresh_token: params.refresh_token,
          });
          if (cancelled || settled) return;
          if (sessionError || !data.session) {
            finish(false, sessionError?.message || EXPIRED_LINK_MESSAGE);
            return;
          }
          finish(true);
          return;
        }

        if (params.token_hash) {
          const { data, error: otpError } = await supabase.auth.verifyOtp({
            token_hash: params.token_hash,
            type: 'recovery',
          });
          if (cancelled || settled) return;
          if (otpError || !data.session) {
            finish(false, otpError?.message || EXPIRED_LINK_MESSAGE);
            return;
          }
          finish(true);
          return;
        }

        if (params.code) {
          const stillInUrl = new URLSearchParams(window.location.search).get('code');
          if (!stillInUrl) {
            const { data: { session } } = await supabase.auth.getSession();
            if (cancelled || settled) return;
            if (session) finish(true);
            else finish(false, EXPIRED_LINK_MESSAGE);
            return;
          }
          const { data, error: codeError } = await supabase.auth.exchangeCodeForSession(params.code);
          if (cancelled || settled) return;
          if (data?.session) {
            finish(true);
            return;
          }
          finish(false, codeError?.message || EXPIRED_LINK_MESSAGE);
          return;
        }

        if (!expectingRecovery) {
          finish(false, EXPIRED_LINK_MESSAGE);
        }
      } catch (err) {
        if (!cancelled && !settled) finish(false, err.message || EXPIRED_LINK_MESSAGE);
      }
    };

    void establish();
    timeoutId = window.setTimeout(() => finish(false, EXPIRED_LINK_MESSAGE), 15000);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      await api.auth.updatePassword(password);
      setUser(null);
      try {
        await api.auth.signOut();
      } catch {
        // Session may already be gone; still send them to login.
      }
      setPasswordRecoveryMode(false);
      setUser(null);
      navigate('/login?password-reset=success', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not update password. Request a new reset email and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const leaveReset = async (to) => {
    try {
      await api.auth.signOut();
    } catch {
      // ignore
    }
    setPasswordRecoveryMode(false);
    setUser(null);
    navigate(to);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    void leaveReset('/login');
  };

  const handleRequestNewLink = (e) => {
    e.preventDefault();
    void leaveReset('/forgot-password');
  };

  if (phase === 'loading') {
    return fullPageSpinner;
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid or expired link</h2>
          <p className="text-sm text-gray-600 mb-6">
            {error || EXPIRED_LINK_MESSAGE}
          </p>
          <div className="flex flex-col gap-3">
            <Link
              to="/forgot-password"
              onClick={handleRequestNewLink}
              className="text-sm font-medium text-primary hover:text-primary-dark"
            >
              Request a new reset email
            </Link>
            <Link to="/login" onClick={handleCancel} className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Back to sign in
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200">
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 bg-primary rounded-lg shadow-sm flex items-center justify-center mb-4">
            <span className="text-white font-bold text-2xl">P</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Choose a new password</h2>
          <p className="mt-2 text-sm text-gray-600">
            <button
              type="button"
              onClick={handleCancel}
              className="font-medium text-primary hover:text-primary-dark"
            >
              Cancel and sign in
            </button>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-500 text-sm rounded-md border border-red-100">{error}</div>
          )}

          <Input
            label="New password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />

          <Input
            label="Confirm password"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />

          <Button type="submit" className="w-full" isLoading={submitting} size="lg">
            Update password
          </Button>
        </form>
      </Card>
    </div>
  );
}
